import { useZero } from "@rocicorp/zero/react";
import { type ComponentProps, type ReactNode } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useQuery } from "../../hooks/useQuery";
import { useUrlSort } from "../../hooks/useUrlSort";
import { formatRelativeTime } from "../../lib/formatRelativeTime";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { userDisplayName } from "../../lib/userDisplayName";
import { Avatar } from "../Avatar";
import { ConnectorLogo } from "../ConnectorLogo";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import {
  type FilterDef,
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
  toggleMultiFilter,
} from "../ListFilter";
import { ListTable } from "../ListTable";
import { SearchField } from "../Settings/shared";
import { SubtleButton } from "../SubtleButton";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { MonitoringLayout } from "./MonitoringLayout";

type RequestRow = {
  key: string;
  name: (hover: boolean) => ReactNode;
  requester: ReactNode;
  createdAt: ReactNode;
  actions: ReactNode;
  link?: string;
};

function requestActions(
  isMine: boolean,
  onApprove: () => void,
  onDismiss: () => void,
  onCancel: () => void
): ReactNode {
  const sections = isMine
    ? [
        {
          items: [
            {
              type: "button" as const,
              left: <Icon name="undo-2" size={16} />,
              label: "Cancel request",
              onClick: onCancel,
            },
          ],
        },
      ]
    : [
        {
          items: [
            {
              type: "button" as const,
              left: <Icon name="check" size={16} />,
              label: "Approve",
              onClick: onApprove,
            },
            {
              type: "button" as const,
              left: <Icon name="x" size={16} />,
              label: "Dismiss",
              onClick: onDismiss,
            },
          ],
        },
      ];
  // stopPropagation so the kebab doesn't trigger the row's open-modal click.
  return (
    <div
      style={{ display: "flex", justifyContent: "flex-end" }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <Dropdown
        opener={<SubtleButton icon="more" variant="secondary" />}
        align="bottom"
        minWidth={160}
        sections={sections}
      />
    </div>
  );
}

function cellText(value: string, color: string): ReactNode {
  return value ? (
    <Text ellipsis variant="text-small" color={color}>
      {value}
    </Text>
  ) : null;
}

function requestedWhen(createdAt: number): ReactNode {
  const relative = formatRelativeTime(createdAt);
  const label = relative.charAt(0).toUpperCase() + relative.slice(1);
  return (
    <Text variant="text-small" color="var(--text-primary)">
      <Tooltip
        content={new Date(createdAt).toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        })}
      >
        {label}
      </Tooltip>
    </Text>
  );
}

function RequestIcon({
  hover,
  requester,
  children,
}: {
  hover: boolean;
  requester: ComponentProps<typeof Avatar>["user"] | null | undefined;
  children: ReactNode;
}) {
  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <ConnectorLogoContainer
        size={32}
        hover={hover}
        borderRadius={8}
        outline={false}
        hoverBackground={false}
      >
        {children}
      </ConnectorLogoContainer>
      {requester ? (
        <div
          style={{
            position: "absolute",
            bottom: -4,
            right: -4,
            borderRadius: "50%",
            boxShadow: "0 0 0 2px var(--cell-bg, var(--surface-primary))",
            lineHeight: 0,
          }}
        >
          <Avatar user={requester} size={16} tooltip={false} />
        </div>
      ) : null}
    </div>
  );
}

export function Requests() {
  const z = useZero();
  const [grantRequests, grantDetails] = useQuery(
    queries.pendingGrantRequests()
  );
  const [currentUser] = useQuery(queries.currentUser());

  const { q, requester } = useCurrentUrl(routes.monitoring.requests);
  const search = q ?? "";
  const setSearch = (value: string) =>
    navigate(null, { q: value }, { replace: true });
  const requesterFilters = parseMultiFilter(requester);
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.monitoring.requests,
    { name: "asc", requester: "asc", createdAt: "desc" }
  );

  const entries: {
    createdAt: number;
    title: string;
    requesterName: string;
    requesterId: string | null;
    requesterUser: ComponentProps<typeof Avatar>["user"] | null;
    item: RequestRow;
  }[] = [];

  for (const request of grantRequests) {
    const connector = request.scopeAsConnector;
    if (!connector) continue;
    const requester = request.requestedByUser
      ? userDisplayName(request.requestedByUser)
      : "Someone";
    const isMine = currentUser?.id === request.requestedByUserId;
    entries.push({
      createdAt: request.createdAt,
      title: connector.name,
      requesterName: requester,
      requesterId: request.requestedByUserId ?? null,
      requesterUser: request.requestedByUser ?? null,
      item: {
        key: `grant:${request.id}`,
        name: (hover) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              minWidth: 0,
            }}
          >
            <RequestIcon hover={hover} requester={request.requestedByUser}>
              <ConnectorLogo app={connector} size={16} faded={false} />
            </RequestIcon>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minWidth: 0,
              }}
            >
              <Text
                ellipsis
                variant="text-regular-md"
                color="var(--text-primary)"
              >
                {connector.name}
              </Text>
              {cellText("Connector access", "var(--text-secondary)")}
            </div>
          </div>
        ),
        requester: cellText(requester, "var(--text-primary)"),
        createdAt: requestedWhen(request.createdAt),
        actions: requestActions(
          isMine,
          () =>
            z.mutate(
              sharedMutators.approveGrantRequest({ requestId: request.id })
            ),
          () =>
            z.mutate(
              sharedMutators.dismissGrantRequest({ requestId: request.id })
            ),
          () =>
            z.mutate(
              sharedMutators.cancelGrantRequest({ requestId: request.id })
            )
        ),
        link: makeUrl(routes.modal.connector, {
          id: connector.id,
          tab: "access",
        }),
      },
    });
  }

  const requestersById = new Map<
    string,
    ComponentProps<typeof Avatar>["user"]
  >();
  for (const entry of entries) {
    if (entry.requesterId && entry.requesterUser)
      requestersById.set(entry.requesterId, entry.requesterUser);
  }
  const requesterOptions = [...requestersById.entries()].sort(([, a], [, b]) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );

  const filters: FilterDef[] = [
    {
      kind: "multi",
      key: "requester",
      label: "Requested by",
      icon: <Icon name="circle-user-round" size={16} />,
      options: requesterOptions.map(([id, user]) => ({
        id,
        label: userDisplayName(user),
        menuLeft: <Avatar user={user} size={16} />,
        pillLeft: <Avatar user={user} size={12} />,
      })),
      values: requesterFilters,
      onToggle: (id) =>
        navigate(
          null,
          { requester: toggleMultiFilter(requesterFilters, id) },
          { replace: true }
        ),
    },
  ];

  const needle = search.trim().toLowerCase();
  const items = entries
    .filter((entry) => {
      if (
        needle &&
        !entry.title.toLowerCase().includes(needle) &&
        !entry.requesterName.toLowerCase().includes(needle)
      )
        return false;
      if (
        requesterFilters.length > 0 &&
        (entry.requesterId == null ||
          !requesterFilters.includes(entry.requesterId))
      )
        return false;
      return true;
    })
    .sort((a, b) => {
      const dir = sortDirection === "asc" ? 1 : -1;
      switch (sortColumn) {
        case "name":
          return a.title.localeCompare(b.title) * dir;
        case "requester":
          return a.requesterName.localeCompare(b.requesterName) * dir;
        case "createdAt":
          return (a.createdAt - b.createdAt) * dir;
        default:
          return b.createdAt - a.createdAt;
      }
    })
    .map((entry) => entry.item);

  const loaded = grantDetails?.type === "complete";

  return (
    <MonitoringLayout
      headerDivider
      headerItem={
        <div style={{ marginLeft: "auto", paddingRight: 12 }}>
          <SearchField
            placeholder="Search requests…"
            value={search}
            onChange={setSearch}
          />
        </div>
      }
    >
      <ListTable
        style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
        columns={{ requester: "md", createdAt: "md" }}
        headers={{
          name: "Request",
          requester: "From",
          createdAt: "When",
          actions: "",
        }}
        sortableColumns={sortableColumns}
        dateColumns={["createdAt"]}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        headerActions={<ListFilter filters={filters} />}
        footer={
          hasActiveFilters(filters) ? (
            <ListFilterPills filters={filters} />
          ) : null
        }
        emptyState={
          loaded ? (
            entries.length > 0 ? (
              <EmptyState
                iconName="list-todo"
                title="No matching requests"
                description="Try a different search or filter."
              />
            ) : (
              <EmptyState
                iconName="heart-handshake"
                title="No pending requests"
                description="Requests will appear here for you to review or track."
              />
            )
          ) : undefined
        }
        items={items}
      />
    </MonitoringLayout>
  );
}
